/**
 * Bind / unbind a project's API workspace (card 1.3, project scope).
 *
 * The rule "a team project may only bind a workspace shared with that team" is
 * checked twice: on bind (refused with a code the UI translates) and on load
 * (`boundWorkspace` returns null for a binding that stopped being valid — the
 * workspace was unshared from the team, deleted, or never existed on this
 * device), so a stale record never opens a workspace the team cannot see.
 */
import { saveProject } from '../graph/projectsStore';
import { teamIdOfScope } from '../graph/graphScopes';
import { personalScopeId } from '../services/personalScope';
import { bindingRefusal, normalizeBinding, withWorkspace, withoutWorkspace } from './projectBindingFields';

function refusal(code) {
  const text = {
    noWorkspace: 'Pick an API workspace to bind.',
    teamWorkspaceNotShared: 'A team project can only bind a workspace shared with that team.',
  };
  return Object.assign(new Error(text[code] || code), { code });
}

const keyOf = (project) => project?.scopeKey || personalScopeId();

/**
 * Adds `workspace` to `project` (`{ id, scopeKey, …the project's fields }`) —
 * a project holds any number of them (21.10.2026). Returns the saved project.
 */
export async function bindWorkspace(project, workspace) {
  const key = keyOf(project);
  const code = bindingRefusal(teamIdOfScope(key), workspace);
  if (code) throw refusal(code);
  return saveProject({ ...project, ...withWorkspace(project, workspace) }, key);
}

/** Takes `workspaceId` out of the project (every workspace when null); the project and the workspace both stay. */
export async function unbindWorkspace(project, workspaceId = null) {
  return saveProject({ ...project, ...withoutWorkspace(project, workspaceId) }, keyOf(project));
}

/** The workspace objects `project` holds that are in `workspaces` and still allowed, in the project's order. */
export function boundWorkspaces(project, workspaces) {
  const teamId = teamIdOfScope(keyOf(project));
  const byId = new Map((workspaces || []).filter(Boolean).map((w) => [String(w.id), w]));
  return normalizeBinding(project || {}).apiWorkspaces
    .map((e) => byId.get(e.id))
    .filter((ws) => ws && !bindingRefusal(teamId, ws));
}

/** The project's first still-valid workspace, or null. */
export function boundWorkspace(project, workspaces) {
  return boundWorkspaces(project, workspaces)[0] || null;
}
